import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, Gift, Timer } from "lucide-react";
import { useFlashSale, useGiftOffer, useLuminaLaunch, fmtLeft, lastHours } from "@/lib/flash";
import { useLang, shopPath } from "@/lib/i18n";
import { euro } from "@/lib/api";
import { LuminaHero } from "./LuminaLaunch";

function CopyCode({ code, testid }) {
  const [done, setDone] = useState(false);
  const { lang } = useLang();
  const copy = () => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setDone(true);
    setTimeout(() => setDone(false), 1800);
  };
  return (
    <button type="button" onClick={copy} data-testid={testid} aria-label={lang === "en" ? `Copy the code ${code}` : `Copier le code ${code}`}
      className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-white/80 px-4 py-1.5 font-bold tracking-[0.25em] text-white transition-colors hover:bg-white/10">
      {code} {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/** Bandeau fixe en haut de toutes les pages pendant la vente flash. */
export function FlashBar() {
  const f = useFlashSale();
  const { lang } = useLang();
  if (!f) return null;
  const en = lang === "en";
  const last = lastHours(f.left, lang);
  const Code = () => <b className="tracking-[0.25em]">{f.code}</b>;
  return (
    <div data-testid="flash-bar" role="region" aria-label={en ? "Flash sale" : "Vente flash"}
      className="flex h-9 items-center justify-center gap-3 overflow-hidden whitespace-nowrap border-b border-black/20 bg-[var(--flash)] px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-white md:gap-4 md:text-xs">
      {last ? <span data-testid="flash-bar-last" className="rounded-full bg-[var(--ink)] px-2.5 py-0.5 text-[var(--gold-soft)]">{last.charAt(0).toUpperCase() + last.slice(1)}!</span> : null}
      <span className={last ? "hidden md:inline" : "md:hidden"}>
        {last ? <>-{f.percent}% {en ? "with code" : "avec le code"} <Code /></> : <>{en ? "Flash:" : "Flash :"} -{f.percent}{en ? "%" : " %"} code <Code /></>}
      </span>
      <span className={last ? "md:hidden" : "hidden md:inline"}>
        {last ? <>code <Code /></> : en ? <>Flash sale: {f.percent}% off the whole shop with code <Code /></> : <>Vente flash : -{f.percent} % sur tout le site avec le code <Code /></>}
      </span>
      <span data-testid="flash-bar-countdown" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--ink)] px-3 py-0.5 font-mono tabular-nums text-[var(--gold-soft)]"><Timer className="h-3.5 w-3.5" aria-hidden="true" />{fmtLeft(f.left)}</span>
    </div>
  );
}

const HERO = {
  fr: { eyebrow: "Vente flash · durée limitée", last: "Dernière ligne droite : ", all: "sur tout le site", end: "avant la fin", cta: "J'en profite",
    body: (p) => `Soins, packs et cures : profitez de -${p} % sur toute la boutique avec le code ci-dessous, à saisir au moment du paiement. Hors abonnements et frais de livraison.` },
  en: { eyebrow: "Flash sale · limited time", last: "Final stretch: ", all: "off everything", end: "left", cta: "Shop now",
    body: (p) => `Hair care, packs and treatment programmes: enjoy ${p}% off the whole shop with the code below, to enter at checkout. Excludes subscriptions and shipping costs.` },
};

/** Bandeau fixe pendant l'offre cadeau 72 h. */
export function GiftBar() {
  const g = useGiftOffer();
  if (!g) return null;
  return (
    <Link to={`/produit/${g.trigger}`} data-testid="gift-bar" aria-label="Offre : Extrême Repouss offert"
      className="flex h-9 items-center justify-center gap-3 overflow-hidden whitespace-nowrap border-b border-black/20 bg-[var(--flash)] px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-white md:text-xs">
      <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="md:hidden">Extrême Repouss offert</span>
      <span className="hidden md:inline">1 Extrême Repouss 125 ml offert pour tout Pack Pouss Performance</span>
      <span data-testid="gift-bar-countdown" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--ink)] px-3 py-0.5 font-mono tabular-nums text-[var(--gold-soft)]"><Timer className="h-3.5 w-3.5" aria-hidden="true" />{fmtLeft(g.left)}</span>
    </Link>
  );
}

function GiftHero({ className }) {
  const g = useGiftOffer();
  if (!g) return null;
  return (
    <section data-testid="gift-hero" className={className}>
      <div className="relative mx-auto grid max-w-6xl items-center gap-6 overflow-hidden rounded-sm bg-[var(--flash)] p-6 text-white md:grid-cols-[1.1fr_1fr] md:p-12">
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/90">Offre 72 h · automatique</p>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">Extrême Repouss 125 ml <span className="italic">offert</span></h2>
          <p className="mt-4 max-w-xl text-base text-white/90">Pour chaque Pack Pouss Performance acheté, un Sérum Extrême Repouss 125 ml est ajouté en cadeau à votre commande. Rien à saisir. Non cumulable avec un code promo.</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <span data-testid="gift-hero-countdown" className="font-mono text-2xl tabular-nums md:text-3xl">{fmtLeft(g.left)}</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white/85">avant la fin</span>
          </div>
          <Link to={`/produit/${g.trigger}`} data-testid="gift-hero-cta" className="group mt-7 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--ink)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--gold-soft)] transition-transform duration-300 hover:-translate-y-0.5">
            Je profite de l'offre <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <img src="/editorial/offre-pouss-performance-email-v2.jpg" alt="Le Pack Pouss Performance et l'Extrême Repouss 125 ml offert" className="relative w-full rounded-sm object-cover" loading="lazy" />
      </div>
    </section>
  );
}

/** Rappel sur la fiche du Pack Pouss Performance pendant l'offre. */
export function GiftNote({ slug }) {
  const g = useGiftOffer();
  if (!g || slug !== g.trigger) return null;
  return (
    <p data-testid="product-gift-note" className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-sm bg-[var(--flash)] px-3 py-2 text-sm font-semibold text-white">
      <Gift className="h-4 w-4" aria-hidden="true" /> + 1 Extrême Repouss 125 ml offert par pack · encore {fmtLeft(g.left)} · sans code, non cumulable
    </p>
  );
}

/** Grosse mise en avant (accueil FR et EN, boutique) pendant la vente flash. */
export function FlashHero({ className = "px-5 pt-10 md:px-10" }) {
  const f = useFlashSale();
  const lumina = useLuminaLaunch();
  const { lang } = useLang();
  if (!f) return <>{lumina ? <LuminaHero className={className} /> : null}{lang === "en" ? null : <GiftHero className={className} />}</>;
  const L = HERO[lang === "en" ? "en" : "fr"];
  const last = lastHours(f.left, lang);
  return (
    <section data-testid="flash-hero" className={className}>
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-sm bg-[var(--flash)] p-8 text-white md:p-14">
        <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/20" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-28 right-40 h-56 w-56 rounded-full bg-black/10" />
        <p className="relative text-xs font-semibold uppercase tracking-[0.3em] text-white/90">{L.eyebrow}</p>
        {last ? <p data-testid="flash-hero-last" className="relative mt-4 inline-block rounded-full bg-[var(--ink)] px-4 py-1.5 text-sm font-bold uppercase tracking-[0.18em] text-[var(--gold-soft)]">{L.last}{last}!</p> : null}
        <h2 className="relative mt-4 font-display text-5xl leading-none md:text-7xl">-{f.percent} % <span className="italic">{L.all}</span></h2>
        <p className="relative mt-5 max-w-xl text-base text-white/90">{L.body(f.percent)}</p>
        <div className="relative mt-7 flex flex-wrap items-center gap-4">
          <CopyCode code={f.code} testid="flash-hero-copy" />
          <span data-testid="flash-hero-countdown" className="font-mono text-2xl tabular-nums md:text-3xl">{fmtLeft(f.left)}</span>
          <span className="text-xs uppercase tracking-[0.2em] text-white/85">{L.end}</span>
        </div>
        <Link to={shopPath(lang)} data-testid="flash-hero-cta"
          className="group relative mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--ink)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--gold-soft)] transition-transform duration-300 hover:-translate-y-0.5">
          {L.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </section>
  );
}

/** Rappel sous le prix d'une fiche produit. */
export function FlashPrice({ price }) {
  const f = useFlashSale();
  const { lang } = useLang();
  if (!f || !price) return null;
  const v = Math.round(price * (100 - f.percent)) / 100;
  return (
    <p data-testid="product-flash-price" className="mt-3 inline-flex flex-wrap items-center gap-2 rounded-sm bg-[var(--flash)] px-3 py-2 text-sm font-semibold text-white">
      {euro(v)} {lang === "en" ? `with code ${f.code} · ${fmtLeft(f.left)} left` : `avec le code ${f.code} · encore ${fmtLeft(f.left)}`}
    </p>
  );
}
